import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Button, makeStyles, Theme, darken } from '@material-ui/core';
import clsx from 'clsx';
import produce from 'immer';
import { ConfigParam, ConfigParamGroup, ConfigParamType, ConfigParamOption, GatewayConfigData } from '@moderepo/mode-apis';
import { SelectInputOption, useModePanelStyle, useModeFormStyle, FontIcon, BaseInfoCompProps, BaseInfoCompActionsSet,
    BaseInfoCompSaveAction, InfoCompPanelHeader, ModeCheckBoxInputField, ModeSelectInputField, ModeTextInputField }
    from '@moderepo/mode-app-components';
import { isConfigParamHidden } from '@moderepo/mode-app-components-internal';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        configParamGroup: {
            width       : '100%',
            border      : `1px solid ${theme.palette.divider}`,
            background  : darken(theme.palette.background.paper, 0.05),
            borderRadius: 4,
            marginBottom: theme.spacing(2),
            position    : 'relative',
            textAlign   : 'center',
            overflow    : 'hidden',
            '& .legend' : {
                padding   : theme.spacing(0, 1),
                fontWeight: 'bold',
                fontSize  : 'larger',
            },
        },
    };
}, { name: 'GenericConfigForm' });



/**
 * Initialize the configData with the existingConfigData or the default values from the configSchema.
 * @param configSchema
 * @param existingConfigData
 */
const useInitializeConfigData = (
    configSchema: readonly ConfigParamGroup[] | undefined, existingConfigData: GatewayConfigData | undefined,
): [GatewayConfigData, React.Dispatch<React.SetStateAction<GatewayConfigData>>] => {
    const [configData, setConfigData] = useState<GatewayConfigData>({});

    useEffect(() => {
        if (configSchema) {

            // if existingConfigData is undefined or it is an empty object
            if (!existingConfigData || Object.keys(existingConfigData).length === 0) {

                // There is no existing config data. This mean we are in CREATE new config mode. So we can initialize the new config with
                // the default values defined in the schema.
                // Create an object where we will copy 'default' values into
                const defaultConfigData = { };

                configSchema.forEach((configGroup: ConfigParamGroup) => {
                    configGroup.params.forEach((configParam: ConfigParam<any>) => {
                        // if this configParam has a default value, copy it into the defaultConfigData
                        // if configParam doesn't have a default value BUT it is a type BOOLEAN and it is required, we will also going to initialize
                        // the param with 'false' value so that the user doesn't have to specifically set it to false by check the checkbox and
                        // then uncheck it.
                        if (configParam.default !== undefined || (configParam.type === ConfigParamType.BOOLEAN && configParam.required)) {
                            if (!defaultConfigData[configGroup.key]) {
                                // create an object for the group if there isn't one already
                                defaultConfigData[configGroup.key] = {};
                            }
                            defaultConfigData[configGroup.key] = {
                                ...defaultConfigData[configGroup.key],
                                [configParam.key]: configParam.default !== undefined ? configParam.default : false,
                            };
                        }
                    });
                });

                setConfigData(defaultConfigData);
            } else {
                // There is already existing configData, don't need to copy 'default' values from the schema to the existingConfigData.
                setConfigData(existingConfigData);
            }
        }
    }, [configSchema, existingConfigData]);

    return [configData, setConfigData];
};



/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface GenericConfigFormUpdatableInputs {
    readonly values: {};
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface GenericConfigFormActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<GenericConfigFormUpdatableInputs>
}


export interface GenericConfigFormProps extends BaseInfoCompProps {

    readonly actions?: GenericConfigFormActionsSet | undefined;

    // The config schema describe what parameters are required for the config and their types
    readonly configSchema: readonly ConfigParamGroup[] | undefined;

    // The configData is the actual values for the config parameters
    readonly configData: GatewayConfigData | undefined;

    // The map of input errors. The structure should be the same as the configSchema and the configData
    readonly configErrors: {} | undefined;

    readonly noDataMessageComp: React.ReactNode;

    readonly onGroupFieldClicked?: (groupName: string, fieldName: string)=> void;
    readonly onGroupInputChange?: (groupName: string, fieldName: string, value: any)=> void;
}


/**
 * This is a DUMB component used for displaying basic generic info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const GenericConfigForm = (props: GenericConfigFormProps) => {

    /**
     * There will only be 1 input value which contains the values for all input.
     * We will initialize the inputValues with the current configData
     */
    const [inputValues, setInputValues] = useInitializeConfigData(props.configSchema, props.configData);


    /**
     * On input change handler for all the inputs. When an input change, we will update the inputValues object accordingly based on the
     * group and the input that was changed.
     */
    const onInputChange = useCallback((groupName: string, inputName: string, value: any) => {
        setInputValues(produce(inputValues, (draft: any) => {
            if (!draft[groupName]) {
                draft[groupName] = {};
            }

            draft[groupName][inputName] = value;
        }));
    }, [inputValues, setInputValues]);


    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setInputValues(props.configData || {});
    }, [props.configData, setInputValues]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        // On submitForm, we need to filter out inputValues for configParams that are hidden. It is possible for inputValues to have values for
        // hidden configParams because the user can set some value for some config to cause some configParam to be visible and then make more
        // changes to the value to make these input hidden.
        const filteredValues = {};
        if (props.configSchema) {
            props.configSchema.forEach((configGroup: ConfigParamGroup) => {
                if (!filteredValues[configGroup.key]) {
                    filteredValues[configGroup.key] = {};
                }
                if (inputValues[configGroup.key]) {
                    configGroup.params.forEach((configParam: ConfigParam<any>) => {
                        const isHidden = isConfigParamHidden(configParam, inputValues[configGroup.key]);
                        if (!isHidden) {
                            // make sure to cast the value into the correct type
                            const configType = configParam.type;
                            switch (configType) {
                                case ConfigParamType.NUMBER:
                                    filteredValues[configGroup.key][configParam.key] = inputValues[configGroup.key]?.[configParam.key] !== undefined
                                        ? Number(inputValues?.[configGroup.key]?.[configParam.key]) : undefined;
                                    break;
                                case ConfigParamType.BOOLEAN:
                                    filteredValues[configGroup.key][configParam.key] = inputValues[configGroup.key]?.[configParam.key] !== undefined
                                        ? Boolean(inputValues[configGroup.key]?.[configParam.key]) : undefined;
                                    break;
                                default:
                                    filteredValues[configGroup.key][configParam.key] = inputValues[configGroup.key]?.[configParam.key];
                            }
                        }
                    });
                }
            });
        }

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                values: filteredValues,
            });
        }

        return false;
    }, [props, inputValues]);



    const createInputFieldByConfigParam = useCallback((paramGroup: ConfigParamGroup, configParam: ConfigParam<any>): JSX.Element | undefined => {

        // InputField only support 1 level therefore it only provide the input's name. We need to capture this event
        // and call the GenericConfigForm' onGroupInputChange handler which take groupName and fieldName
        const onInputChangeEventCapture = (_: string, value: any) => {
            if (props.onGroupInputChange) {
                props.onGroupInputChange(paramGroup.key, configParam.key, value);
            }
        };

        // InputField only support 1 level therefore it only provide the input's name. We need to capture this event
        // and call the GenericConfigForm' onGroupEditableInputClicked handler which take groupName and fieldName
        const onEditableInputClickedEventCapture = () => {
            if (props.onGroupFieldClicked) {
                props.onGroupFieldClicked(paramGroup.key, configParam.key);
            }
        };


        // If the configParam depends on other config, make it hidden if the other configs' values are not satisfied
        const isHidden = isConfigParamHidden(configParam, inputValues[paramGroup.key]);


        // Get the current input value from the inputValues object and pass it to the input field
        const currentInputValue = inputValues[paramGroup.key] ? inputValues[paramGroup.key]?.[configParam.key] : undefined;

        const currentInputError = props.configErrors && props.configErrors[paramGroup.key]
            ? props.configErrors[paramGroup.key][configParam.key] : undefined;

        switch (configParam.type) {
            case ConfigParamType.STRING: {
                if (configParam.options) {
                    // Create SELECT input field
                    return (
                        <ModeSelectInputField
                            onFieldClicked={onEditableInputClickedEventCapture}
                            onFormInputChange={onInputChangeEventCapture}
                            isInEditMode={props.isEditing}
                            label={configParam.label}
                            description={configParam.description}
                            error={currentInputError}
                            editable
                            hidden={isHidden}
                            options={configParam.options.map((option: ConfigParamOption<string>): SelectInputOption<string> => {
                                return {
                                    label: option.label,
                                    value: option.value,
                                } as SelectInputOption<string>;
                            })}
                            className={formClasses.formInputField}
                            fieldName={configParam.key}
                            value={currentInputValue}
                            onInputChange={(inputName: string, newInputValue: any) => {
                                onInputChange(paramGroup.key, configParam.key, newInputValue);
                            }}
                        />
                    );
                }
                return (
                    <ModeTextInputField
                        inputType="text"
                        onFieldClicked={onEditableInputClickedEventCapture}
                        onFormInputChange={onInputChangeEventCapture}
                        isInEditMode={props.isEditing}
                        label={configParam.label}
                        description={configParam.description}
                        error={currentInputError}
                        editable
                        hidden={isHidden}
                        className={formClasses.formInputField}
                        fieldName={configParam.key}
                        value={currentInputValue}
                        onInputChange={(inputName: string, newInputValue: any) => {
                            onInputChange(paramGroup.key, configParam.key, newInputValue);
                        }}
                    />
                );

            }
            case ConfigParamType.NUMBER: {
                if (configParam.options) {
                    return (
                        <ModeSelectInputField
                            onFieldClicked={onEditableInputClickedEventCapture}
                            onFormInputChange={onInputChangeEventCapture}
                            isInEditMode={props.isEditing}
                            label={configParam.label}
                            description={configParam.description}
                            error={currentInputError}
                            editable
                            hidden={isHidden}
                            options={configParam.options.map((option: ConfigParamOption<number>): SelectInputOption<number> => {
                                return {
                                    label: option.label,
                                    value: option.value,
                                } as SelectInputOption<number>;
                            })}
                            className={formClasses.formInputField}
                            fieldName={configParam.key}
                            value={currentInputValue}
                            onInputChange={(inputName: string, newInputValue: any) => {
                                onInputChange(paramGroup.key, configParam.key, newInputValue);
                            }}
                        />
                    );
                }
                return (
                    <ModeTextInputField
                        inputType="number"
                        onFieldClicked={onEditableInputClickedEventCapture}
                        onFormInputChange={onInputChangeEventCapture}
                        isInEditMode={props.isEditing}
                        label={configParam.label}
                        description={configParam.description}
                        error={currentInputError}
                        editable
                        hidden={isHidden}
                        className={formClasses.formInputField}
                        fieldName={configParam.key}
                        value={currentInputValue}
                        onInputChange={(inputName: string, newInputValue: any) => {
                            onInputChange(paramGroup.key, configParam.key, newInputValue);
                        }}
                    />
                );
            }
            case ConfigParamType.BOOLEAN: {
                return (
                    <ModeCheckBoxInputField
                        onFieldClicked={onEditableInputClickedEventCapture}
                        onFormInputChange={onInputChangeEventCapture}
                        isInEditMode={props.isEditing}
                        label={configParam.label}
                        description={configParam.description}
                        error={currentInputError}
                        editable
                        hidden={isHidden}
                        className={formClasses.formInputField}
                        fieldName={configParam.key}
                        value={currentInputValue as boolean}
                        onInputChange={(inputName: string, newInputValue: any) => {
                            onInputChange(paramGroup.key, configParam.key, newInputValue);
                        }}
                    />
                );
            }
            default: {
                return (
                    <div key={configParam.key} />
                );
            }
        }

    }, [props, onInputChange, formClasses.formInputField, inputValues]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className, classes.root)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                {props.configData && (
                    <form
                        className={clsx(formClasses.root, props.formProps?.className)}
                        noValidate
                        autoComplete="on"
                        method="POST"
                        onSubmit={onFormSubmit}
                    >
                        {props.configSchema && (
                            props.configSchema.map((configParamGroup: ConfigParamGroup) => {
                                return (
                                    <fieldset
                                        className={classes.configParamGroup}
                                        key={configParamGroup.key}
                                    >
                                        <legend className="legend">{configParamGroup.label}</legend>
                                        <div
                                            className={clsx(formClasses.formInputGroup)}
                                        >
                                            {configParamGroup.params.map((configParam: ConfigParam<any>) => {
                                                return createInputFieldByConfigParam(configParamGroup, configParam);
                                            })}
                                        </div>
                                    </fieldset>
                                );
                            })
                        )}
                        {props.isEditing && props.actions && (
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div />
                                <div>
                                    {props.actions?.cancelEdit && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            size="small"
                                            startIcon={props.actions.cancelEdit.icon
                                                ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                            onClick={() => {
                                                reverseFormInput();
                                                if (props.actions?.cancelEdit) {
                                                    props.actions.cancelEdit.onClick();
                                                }
                                            }}
                                        >
                                            {props.actions.cancelEdit.label}
                                        </Button>
                                    )}
                                    {props.actions?.saveUpdate && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            startIcon={props.actions.saveUpdate.icon
                                                ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                        >
                                            {props.actions.saveUpdate.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                )}

                {!props.configData && props.noDataMessageComp}
            </div>

        </Paper>
    );

};
